import React, { useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { Home, Image as ImageIcon, User, Plus, LayoutTemplate } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { clsx } from 'clsx';
import { ImageRequirementsModal } from './ImageRequirementsModal';
import { APP_SPRING, APP_SPRING_GENTLE } from '../utils/motion';

export const BottomNav: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const prefersReducedMotion = useReducedMotion();
    const [showRequirements, setShowRequirements] = useState(false);

    const isActive = (path: string) => location.pathname === path;
    const isTemplatesActive = isActive('/templates') || location.pathname.startsWith('/templates/');
    const navItems = [
        { path: '/', label: 'Home', icon: Home, active: isActive('/'), sound: 'navigate' as const },
        { path: '/templates', label: 'Templates', icon: LayoutTemplate, active: isTemplatesActive, sound: 'navigate' as const },
        { path: '/gallery', label: 'Gallery', icon: ImageIcon, active: isActive('/gallery'), sound: 'navigate' as const },
        { path: '/profile', label: 'Profile', icon: User, active: isActive('/profile'), sound: 'navigate' as const }
    ];

    const triggerFilePicker = () => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'image/*';
        input.onchange = (e) => {
            const file = (e.target as HTMLInputElement).files?.[0];
            if (file) {
                const reader = new FileReader();
                reader.onloadend = () => {
                    const base64data = reader.result as string;
                    navigate('/canvas', { state: { imageUrl: base64data } });
                };
                reader.readAsDataURL(file);
            }
        };
        input.click();
        setShowRequirements(false);
    };

    return (
        <>
            <ImageRequirementsModal
                isOpen={showRequirements}
                onClose={() => setShowRequirements(false)}
                onContinue={triggerFilePicker}
            />
            <motion.div
                initial={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, y: 28, scale: 0.96 }}
                animate={prefersReducedMotion ? { opacity: 1 } : { opacity: 1, y: 0, scale: 1 }}
                transition={prefersReducedMotion ? { duration: 0.18 } : APP_SPRING_GENTLE}
                className="bottom-nav-shell fixed bottom-[max(1rem,env(safe-area-inset-bottom))] left-1/2 -translate-x-1/2 w-[90%] max-w-sm z-30"
            >
                <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-xl rounded-[32px] shadow-2xl shadow-indigo-500/10 dark:shadow-black/20 border border-white/50 dark:border-gray-700/50 px-5 py-3 flex items-center justify-between relative overflow-visible transition-theme">
                    {!prefersReducedMotion && (
                        <motion.div
                            aria-hidden="true"
                            className="pointer-events-none absolute inset-x-8 top-0 h-10 rounded-full bg-linear-to-r from-indigo-300/0 via-indigo-300/25 to-purple-300/0 blur-2xl"
                            animate={{ x: [-18, 18, -18], opacity: [0.25, 0.4, 0.25] }}
                            transition={{ duration: 6.5, repeat: Infinity, ease: 'easeInOut' }}
                        />
                    )}

                    {navItems.slice(0, 2).map((item) => {
                        const Icon = item.icon;

                        return (
                            <motion.button
                                key={item.path}
                                type="button"
                                data-sound={item.sound}
                                whileTap={{ scale: 0.92 }}
                                whileHover={prefersReducedMotion ? undefined : { y: -2 }}
                                onClick={() => navigate(item.path)}
                                className={clsx(
                                    'relative flex h-14 w-14 items-center justify-center rounded-2xl transition-colors duration-300',
                                    item.active
                                        ? 'text-indigo-600 dark:text-indigo-400'
                                        : 'text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300'
                                )}
                            >
                                {item.active && (
                                    <>
                                        <motion.span
                                            layoutId="bottom-nav-glow"
                                            className="absolute inset-0 rounded-2xl bg-indigo-50 dark:bg-indigo-500/15"
                                            transition={APP_SPRING}
                                        />
                                        <motion.span
                                            layoutId="bottom-nav-dot"
                                            className="absolute -bottom-1 h-1.5 w-1.5 rounded-full bg-indigo-600 dark:bg-indigo-400"
                                            transition={APP_SPRING}
                                        />
                                    </>
                                )}
                                <motion.div
                                    animate={{ y: item.active ? -2 : 0, scale: item.active ? 1.06 : 1 }}
                                    transition={APP_SPRING}
                                    className="relative z-10"
                                >
                                    <Icon size={24} strokeWidth={item.active ? 2.5 : 2} />
                                </motion.div>
                                <span className="sr-only">{item.label}</span>
                            </motion.button>
                        );
                    })}

                    <div className="relative -top-6 shrink-0">
                        {!prefersReducedMotion && (
                            <motion.span
                                aria-hidden="true"
                                className="absolute inset-0 rounded-full bg-indigo-500/35 blur-xl"
                                animate={{ scale: [0.9, 1.12, 0.9], opacity: [0.3, 0.55, 0.3] }}
                                transition={{ duration: 2.8, repeat: Infinity, ease: 'easeInOut' }}
                            />
                        )}
                        <motion.button
                            type="button"
                            data-sound="select"
                            aria-label="Create new drawing"
                            whileTap={{ scale: 0.92 }}
                            whileHover={prefersReducedMotion ? undefined : { y: -3, scale: 1.03 }}
                            onClick={() => setShowRequirements(true)}
                            className="w-16 h-16 bg-linear-to-tr from-indigo-600 to-purple-600 text-white rounded-full shadow-xl shadow-indigo-500/40 flex items-center justify-center border-4 border-[#F8FAFC] dark:border-gray-900"
                        >
                            <motion.div
                                animate={{ rotate: showRequirements ? 45 : 0 }}
                                transition={APP_SPRING}
                            >
                                <Plus size={32} />
                            </motion.div>
                        </motion.button>
                    </div>

                    {navItems.slice(2).map((item) => {
                        const Icon = item.icon;

                        return (
                            <motion.button
                                key={item.path}
                                type="button"
                                data-sound={item.sound}
                                whileTap={{ scale: 0.92 }}
                                whileHover={prefersReducedMotion ? undefined : { y: -2 }}
                                onClick={() => navigate(item.path)}
                                className={clsx(
                                    'relative flex h-14 w-14 items-center justify-center rounded-2xl transition-colors duration-300',
                                    item.active
                                        ? 'text-indigo-600 dark:text-indigo-400'
                                        : 'text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300'
                                )}
                            >
                                {item.active && (
                                    <>
                                        <motion.span
                                            layoutId="bottom-nav-glow"
                                            className="absolute inset-0 rounded-2xl bg-indigo-50 dark:bg-indigo-500/15"
                                            transition={APP_SPRING}
                                        />
                                        <motion.span
                                            layoutId="bottom-nav-dot"
                                            className="absolute -bottom-1 h-1.5 w-1.5 rounded-full bg-indigo-600 dark:bg-indigo-400"
                                            transition={APP_SPRING}
                                        />
                                    </>
                                )}
                                <motion.div
                                    animate={{ y: item.active ? -2 : 0, scale: item.active ? 1.06 : 1 }}
                                    transition={APP_SPRING}
                                    className="relative z-10"
                                >
                                    <Icon size={24} strokeWidth={item.active ? 2.5 : 2} />
                                </motion.div>
                                <span className="sr-only">{item.label}</span>
                            </motion.button>
                        );
                    })}
                </div>
            </motion.div>
        </>
    );
};
